import { Link } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime, formatTimestampPrecise } from '@/lib/dates'
import { formatTicketRef } from '@/lib/ticketRef'
import { useEmailLogEntry } from '@/queries/emailLog'
import type { EmailLogDetail, EmailLogEvent } from '@/types/emailLog'
import { emailStatusLabels, emailStatusVariants, reasonLabel } from './emailStatus'

const EVENT_LABELS: Record<string, string> = {
  Send: 'Accepted by SES',
  Delivery: 'Delivered',
  Open: 'Opened',
  Click: 'Link clicked',
  Bounce: 'Bounced',
  Complaint: 'Marked as spam by the recipient',
  Reject: 'Rejected by SES',
  DeliveryDelay: 'Delivery delayed',
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[96px_1fr] items-baseline gap-x-4 text-etn-control">
      <span className="text-etn-text-soft">{label}</span>
      <span className="min-w-0 break-words text-etn-text-cell">{children}</span>
    </div>
  )
}

function eventDetail(event: EmailLogEvent): string | null {
  const d = event.detail ?? {}
  if (event.type === 'Click' && d.link) return d.link
  const parts = [d.bounceType, d.bounceSubType, d.diagnosticCode, d.complaintFeedbackType, d.reason, d.delayType].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

function Timeline({ entry }: { entry: EmailLogDetail }) {
  if (!entry.events.length) {
    return <p className="text-etn-control text-etn-text-soft">No delivery events recorded.</p>
  }
  return (
    <ol className="space-y-2.5 border-l border-etn-border-hairline pl-4" aria-label="Event timeline">
      {entry.events.map(event => {
        const detail = eventDetail(event)
        return (
          <li key={event.eventId} className="text-etn-control">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-semibold text-etn-text-cell">{EVENT_LABELS[event.type] ?? event.type}</span>
              <time dateTime={event.at} className="shrink-0 text-etn-micro tabular-nums text-etn-text-soft">
                {formatTimestampPrecise(event.at)}
              </time>
            </div>
            {detail && <p className="mt-0.5 break-all text-etn-micro text-etn-text-soft">{detail}</p>}
          </li>
        )
      })}
    </ol>
  )
}

function Body({ entry }: { entry: EmailLogDetail }) {
  const outbound = entry.direction === 'outbound'
  const reason = reasonLabel(entry.reason)
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Row label="Status">
          <Badge variant={emailStatusVariants[entry.status]}>{emailStatusLabels[entry.status]}</Badge>
        </Row>
        {reason && <Row label="Reason">{reason}</Row>}
        <Row label={outbound ? 'Sent' : 'Received'}>{formatDateTime(entry.createdAt)}</Row>
        <Row label="From">{entry.from || '—'}</Row>
        <Row label="To">{entry.to.join(', ') || '—'}</Row>
        {entry.kind && <Row label="Kind">{entry.kind}</Row>}
        {entry.ticket && (
          <Row label="Ticket">
            <Link to={`/admin/tickets/${entry.ticket._id}` as never} className="font-semibold text-etn-accent-text-active hover:underline">
              {formatTicketRef(entry.ticket)}
            </Link>{' '}
            <span className="text-etn-text-soft">{entry.ticket.subject}</span>
          </Row>
        )}
        {outbound && (
          <Row label="Opens">
            {/* Approximate: blocked images under-count, Apple Mail Privacy Protection over-counts. */}
            {entry.openCount}
            {entry.firstOpenedAt ? `, first ${formatDateTime(entry.firstOpenedAt)}` : ''}
            <span className="ml-1 text-etn-micro text-etn-text-soft">(approximate)</span>
          </Row>
        )}
        {outbound && entry.clickCount > 0 && <Row label="Clicks">{entry.clickCount}</Row>}
        {entry.sesMessageId && <Row label="SES id"><span className="font-mono text-etn-micro">{entry.sesMessageId}</span></Row>}
        {entry.rawS3Key && <Row label="Raw email"><span className="font-mono text-etn-micro">{entry.rawS3Key}</span></Row>}
      </div>
      {outbound && (
        <section>
          <h3 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Timeline</h3>
          <Timeline entry={entry} />
        </section>
      )}
    </div>
  )
}

export function EmailDetailDialog({ id, onClose }: { id?: string; onClose: () => void }) {
  const { data, isLoading, error } = useEmailLogEntry(id)
  return (
    <Dialog open={Boolean(id)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="pr-6 leading-snug">{data?.subject || (isLoading ? 'Loading…' : '(no subject)')}</DialogTitle>
          <DialogDescription>{data ? (data.direction === 'outbound' ? 'Outbound notification' : 'Inbound email') : ' '}</DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-40 w-full" />}
        {error && <p className="text-etn-control text-etn-danger-text">Couldn't load this email.</p>}
        {data && <Body entry={data} />}
      </DialogContent>
    </Dialog>
  )
}
